import { useState, useEffect, useCallback, useMemo } from 'react'
import { useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { fetchUiLabelsForLanguage } from '@/services/uiLabelsApiService'
import { getLangFromPathname } from '@/utils/localeFromUrl'

/**
 * Provides UI labels (header, footer) with admin overrides from the API (DB).
 * Language follows the URL (/ar/..., /en/...) so labels match the visible locale.
 */
export function useUiLabels() {
  const { pathname } = useLocation()
  const { i18n } = useTranslation()
  const lang = getLangFromPathname(pathname)
  const [labels, setLabels] = useState<Record<string, string>>({})
  const tBundle = useMemo(() => i18n.getFixedT(lang, 'translation'), [i18n, lang])

  useEffect(() => {
    let cancelled = false
    fetchUiLabelsForLanguage(lang).then((data) => {
      if (!cancelled) setLabels(data)
    })
    return () => { cancelled = true }
  }, [lang])

  const getLabel = useCallback(
    (key: string, fallback?: string): string => {
      const fromApi = labels[key]
      // Ignore empty or mistaken rows where Value equals the key (overrides would show raw keys).
      if (fromApi != null && fromApi !== '' && fromApi !== key) return fromApi
      const fromI18n = tBundle(key)
      if (fromI18n != null && fromI18n !== key) return fromI18n
      return fallback ?? key
    },
    [labels, tBundle]
  )

  return { getLabel, labels }
}
